// Estado da conexão com o servidor do PDV. navigator.onLine sozinho não
// basta (fica `true` num computador ligado na rede local mas sem internet),
// então quem manda aqui é o resultado real das requisições: qualquer
// chamada à API que falhe sem resposta do servidor (rede caída, timeout)
// marca offline (ver ErrorInterceptor), e enquanto estiver offline um ping
// periódico ao servidor detecta a volta.
//
// Enquanto offline, os serviços (ver servico.ts) nem tentam a rede — vão
// direto pro banco local, sem o operador esperar o timeout de 20s a cada
// busca de produto.
//
// Este arquivo não pode importar nada do app (nem ./plataforma): ele é
// importado pelos interceptors do axios, e plataforma importa o axios — o
// ciclo quebra o carregamento ("Cannot access 'Api' before initialization").
// Por isso a URL do ping vem direto de window.env (mesma chave nos dois apps).

type TOuvinte = (online: boolean) => void;

const INTERVALO_PING_MS = 2000;
const TIMEOUT_PING_MS = 3000;

let online = typeof navigator === 'undefined' ? true : navigator.onLine;
const ouvintes = new Set<TOuvinte>();
let timerPing: ReturnType<typeof setInterval> | null = null;

const notificar = () => ouvintes.forEach((ouvinte) => ouvinte(online));

const definir = (novo: boolean) => {
  if (novo === online) return;
  online = novo;
  if (online) pararPing();
  else iniciarPing();
  notificar();
};

// Ping sem autenticação e fora do axios (o interceptor dele desloga em
// 401/404). A rota "/" do backend responde com CORS liberado, então dá pra
// ler o status: só conta como online se o BACKEND respondeu (2xx). Antes era
// no-cors e qualquer resposta valia — com o backend fora do ar o nginx
// devolve 502, o PDV achava que tinha voltado, tentava sincronizar, falhava
// e caía de novo pra offline, em loop, martelando o servidor.
const pingar = async (): Promise<boolean> => {
  const controle = new AbortController();
  const timeout = setTimeout(() => controle.abort(), TIMEOUT_PING_MS);
  try {
    const resposta = await fetch(`${window.env.REACT_APP_URL_BASE}/?ping=${Date.now()}`, {
      cache: 'no-store',
      signal: controle.signal,
    });
    return resposta.ok;
  } catch {
    return false;
  } finally {
    clearTimeout(timeout);
  }
};

const iniciarPing = () => {
  if (timerPing) return;
  // Já testa na hora (não espera o primeiro INTERVALO_PING_MS) — se a internet
  // já tiver voltado no instante em que caímos pra offline, sai do modo
  // offline imediatamente, sem esperar o próximo ciclo do timer.
  pingar().then((alcancavel) => { if (alcancavel) definir(true); });
  timerPing = setInterval(async () => {
    if (await pingar()) definir(true);
  }, INTERVALO_PING_MS);
};

const pararPing = () => {
  if (!timerPing) return;
  clearInterval(timerPing);
  timerPing = null;
};

if (typeof window !== 'undefined') {
  window.addEventListener('offline', () => definir(false));
  window.addEventListener('online', () => {
    pingar().then(definir);
  });
  if (!online) iniciarPing();
}

export const conexao = {
  estaOnline: () => online,
  estaOffline: () => !online,
  marcarOffline: () => definir(false),
  marcarOnline: () => definir(true),
  // Confere de verdade agora (ping) e atualiza o estado.
  verificar: async (): Promise<boolean> => {
    const alcancavel = await pingar();
    definir(alcancavel);
    return alcancavel;
  },
  inscrever: (ouvinte: TOuvinte) => {
    ouvintes.add(ouvinte);
    return () => { ouvintes.delete(ouvinte); };
  },
};
